import { Cesium3DTileset, PrimitiveCollection, Viewer } from "cesium";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  DerivedObservableProperty,
  ObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";
import {
  compareToolState,
  SerializableToolInterface,
} from "./SerializableToolInterface";

export type LodPresetName = "fine" | "medium" | "coarse";

export type LodPresetMapping = {
  name: LodPresetName;
  pointcloud: {
    maxScreenSpaceError: number;
    geometricErrorScale: number;
    skipLevelOfDetail: boolean;
    skipScreenSpaceErrorFactor: number;
    skipLevels: number;
  };
  model: {};
};

const LOD_PRESET_FINE: LodPresetMapping = {
  name: "fine",
  pointcloud: {
    maxScreenSpaceError: 16,
    geometricErrorScale: 1,
    skipLevelOfDetail: false,
    skipScreenSpaceErrorFactor: 16,
    skipLevels: 0,
  },
  model: {},
};
const LOD_PRESET_MEDIUM: LodPresetMapping = {
  name: "medium",
  pointcloud: {
    maxScreenSpaceError: 32,
    geometricErrorScale: 2,
    skipLevelOfDetail: true,
    skipScreenSpaceErrorFactor: 64,
    skipLevels: 3,
  },
  model: {},
};
const LOD_PRESET_COARSE: LodPresetMapping = {
  name: "coarse",
  pointcloud: {
    maxScreenSpaceError: 64,
    geometricErrorScale: 4,
    skipLevelOfDetail: true,
    skipScreenSpaceErrorFactor: 128,
    skipLevels: 5,
  },
  model: {},
};

export const LOD_PRESETS: Record<LodPresetName, LodPresetMapping> = {
  fine: LOD_PRESET_FINE,
  medium: LOD_PRESET_MEDIUM,
  coarse: LOD_PRESET_COARSE,
};

export const DEFAULT_LOD_PRESET: LodPresetMapping = LOD_PRESETS.fine;

export type LodPresetToolSnapshot = {
  version: 1;
  name: LodPresetName;
};

function isLodPresetStateApplied(
  state: LodPresetToolSnapshot | undefined,
): boolean {
  return !!state && state.name !== DEFAULT_LOD_PRESET.name;
}

export default class LevelOfDetailPresetToolPlugin
  implements
    AttachablePluginInterface,
    BackgroundableToolInterface,
    SerializableToolInterface<LodPresetToolSnapshot>
{
  private _viewer: Viewer;
  private readonly _loadedPointclouds = new Set<Cesium3DTileset>();
  private _debug: boolean = false;

  private readonly _activeLod = new ObservableProperty<LodPresetMapping>(
    DEFAULT_LOD_PRESET,
  );
  readonly isApplied = new DerivedObservableProperty(
    () => this.isStateApplied(this.capture()),
    [this._activeLod],
  );

  constructor() {
    this.onPrimitiveAdded = this.onPrimitiveAdded.bind(this);
    this.applyPointcloudLod = this.applyPointcloudLod.bind(this);
  }

  get activeLod(): ReadonlyObservableProperty<LodPresetMapping> {
    return this._activeLod;
  }

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
    if (viewer) {
      this._viewer.scene.primitives.primitiveAdded.addEventListener(
        this.onPrimitiveAdded,
      );
      this._loadedPointclouds.forEach(this.applyPointcloudLod);
    }
  }

  detachViewer() {
    this._loadedPointclouds.clear();
    if (this._viewer) {
      this._viewer.scene.primitives.primitiveAdded.removeEventListener(
        this.onPrimitiveAdded,
      );
    }
    this._viewer = null;
  }

  isStateApplied(state: LodPresetToolSnapshot | undefined): boolean {
    return isLodPresetStateApplied(state);
  }

  isMatchingState(state: LodPresetToolSnapshot | undefined): boolean {
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  private onPrimitiveAdded(primitive: PrimitiveCollection | Cesium3DTileset) {
    const isTileset = (primitive as Cesium3DTileset).isCesium3DTileset;
    if (isTileset) {
      const tileset = primitive as Cesium3DTileset;
      const isPointcloud = tileset.style?.style?.pointSize !== undefined;
      if (isPointcloud) {
        this._loadedPointclouds.add(primitive as Cesium3DTileset);
        this.applyPointcloudLod(tileset);
        this._viewer?.scene.requestRender();
      }
    }
  }

  private applyPointcloudLod(tileset: Cesium3DTileset) {
    tileset.maximumScreenSpaceError =
      this._activeLod.getSnapshot().pointcloud.maxScreenSpaceError;

    tileset.pointCloudShading.geometricErrorScale =
      this._activeLod.getSnapshot().pointcloud.geometricErrorScale;

    tileset.skipLevelOfDetail =
      this._activeLod.getSnapshot().pointcloud.skipLevelOfDetail;

    tileset.skipScreenSpaceErrorFactor =
      this._activeLod.getSnapshot().pointcloud.skipScreenSpaceErrorFactor;

    tileset.skipLevels = this._activeLod.getSnapshot().pointcloud.skipLevels;

    if (this._debug) {
      tileset.debugShowGeometricError = true;
    }
  }

  setLevelOfDetailPreset(presetName: LodPresetName) {
    this._activeLod.set(LOD_PRESETS[presetName]);
    if (this._viewer) {
      this._loadedPointclouds.forEach(this.applyPointcloudLod);
      this._viewer.scene.requestRender();
    }
  }

  capture(): LodPresetToolSnapshot | undefined {
    return {
      version: 1,
      name: this._activeLod.getSnapshot().name,
    };
  }

  restore(snapshot: LodPresetToolSnapshot | undefined): void {
    if (!this._viewer || !snapshot) return;
    const preset =
      Object.values(LOD_PRESETS).find((p) => p.name === snapshot.name) ??
      DEFAULT_LOD_PRESET;
    this._activeLod.set(preset);
    if (this._viewer) {
      this._loadedPointclouds.forEach(this.applyPointcloudLod);
      this._viewer.scene.requestRender();
    }
  }

  clear(): void {
    this._activeLod.set(DEFAULT_LOD_PRESET);
    if (this._viewer) {
      this._loadedPointclouds.forEach(this.applyPointcloudLod);
      this._viewer.scene.requestRender();
    }
  }
}
